import * as Sentry from '@sentry/vue'

// Third-party pixel script path fragments whose fetch failures are not actionable.
// Sentry's fetch instrumentation captures unhandled rejections from these scripts;
// we suppress them to avoid noise. Extend this list to cover additional pixels.
const THIRD_PARTY_FRAME_PATTERNS = [
  '/ct/lib/', // Criteo conversion pixel (s.pinimg.com/ct/lib/ and similar)
]

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const {
    public: { sentry: config },
  } = useRuntimeConfig()

  if (!config.dsn) {
    return
  }

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn: config.dsn,
    environment: config.environment,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
    ],

    tracesSampleRate: Number(config.clientTraceRate ?? 0.2),

    tracePropagationTargets: ['localhost', /^\//], // This is the default Sentry value

    beforeSend: (event, hint) => {
      console.error(hint.originalException || hint.syntheticException, event)

      const msg = event.exception?.values?.[0]?.value ?? ''

      // Any fetch failure on /_nuxt/builds/meta URLs is noise from stale browser
      // instances trying to load a chunk manifest for an old Vercel deployment —
      // the hash no longer resolves on the CDN. Covers 429 (CDN rate-limiting)
      // and network-level "Load failed" errors alike. Not actionable.
      if (msg.includes('/_nuxt/builds/meta')) return null

      // The McLaren GLP page (/en-gb/mclaren) contains a CMS-managed inline script
      // that calls window.webkit.messageHandlers (a WKWebView-only iOS API) without
      // guarding for the browser context. The script is not in our codebase so we
      // cannot fix it at source; users are not functionally impacted.
      if (msg.includes('window.webkit.messageHandlers')) return null

      const allFrames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? []
      // App frames: non-node_modules, non-pixel-script filenames
      const appFrames = allFrames.filter((f) => f.filename && !f.filename.includes('node_modules'))
      const isThirdPartyFrame = (filename: string) =>
        THIRD_PARTY_FRAME_PATTERNS.some((p) => filename.includes(p))
      const firstPartyFrames = appFrames.filter((f) => !f.filename || !isThirdPartyFrame(f.filename))

      // Errors whose only application frames are from known third-party pixel scripts
      // (e.g. Criteo /ct/lib/) are fetch noise captured by Sentry's instrumentation
      // but not actionable from our codebase
      if (appFrames.length > 0 && appFrames.every((f) => f.filename && isThirdPartyFrame(f.filename)))
        return null

      // Browser/network errors with no first-party code in the stack are not actionable:
      // - "Load failed": iOS Safari unhandled fetch rejections
      // - "Failed to fetch": third-party pixel fetch failures with no stacktrace
      // - "Importing a module script failed": iOS module load failures in in-app browsers
      //   such as Instagram — a browser compatibility issue we cannot fix
      // Use includes() for 'Failed to fetch' to handle minor message format variations
      // across browsers (e.g. iOS in-app browsers, Snapchat WebView) that may wrap
      // the value with additional context. The firstPartyFrames guard ensures we only
      // suppress events with no first-party code in the stack.
      if (
        firstPartyFrames.length === 0 &&
        (msg === 'Load failed' ||
          msg.includes('Failed to fetch') ||
          msg.startsWith('Importing a module script failed'))
      )
        return null

      // SDK 9 stopped inferring IP addresses unless sendDefaultPii is set, and setting it
      // would also attach request headers, cookies and bodies. We want the IP for triage and
      // nothing else, so ask the ingest to infer only that field.
      event.user = { ...event.user, ip_address: '{{auto}}' }

      return event
    },
  })

  return {
    provide: {
      sentry: {
        captureException: (exception) => {
          return Sentry.captureException(exception?.value || exception)
        },
        captureMessage: Sentry.captureMessage,
        withScope: Sentry.withScope,
      },
    },
  }
})
